'use client';

import { Box, Typography } from '@mui/material';
import Image from 'next/image';

import OrangeLine from '@/components/atoms/OrangeLine';
import { SectionContainer } from '@/components/layouts/SectionContainer';
import { Partners } from '@/services/types';
import { getImageUrl } from '@/services/utils';

interface PartnersSectionProps {
  data?: Partners;
}

const PartnersSection = ({ data }: PartnersSectionProps) => {
  const fallbackData: Partners = {
    id: 1,
    title: 'ĐỐI TÁC',
    subtitle: 'Những đối tác tin cậy đã đồng hành cùng chúng tôi',
    autoPlay: true,
    autoPlayInterval: 5000,
    partners: [
      {
        id: 1,
        name: 'Partner 1',
        logo: {
          id: 1,
          name: 'Partner 1',
          url: '/api/uploads/partner-1.png',
          width: 100,
          height: 56,
        },
        website: 'https://partner1.com',
        order: 1,
        isActive: true,
      },
    ],
  };

  const partners = data || fallbackData;
  const partnerItems = partners.partners || [];

  return (
    <SectionContainer
      sx={{
        pl: 0,
        pr: 0,
      }}
    >
      <Box
        component='section'
        id='partners-section'
        data-component='partners-section'
        sx={{
          backgroundColor: 'white',
          width: '100%',
          py: { xs: 6, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {/* Section Title */}
        <Box
          id='partners-header'
          data-component='partners-header'
          sx={{
            textAlign: 'left',
            mb: 6,
            pl: { xs: 3, sm: 4, md: '160px', lg: '345px' },
            pr: { xs: 2, md: 4, lg: 0 },
          }}
        >
          <Typography
            variant='h2'
            component='h2'
            id='partners-title'
            data-component='partners-title'
            sx={{
              fontFamily: 'Radio Canada, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '32px', md: '40px' },
              lineHeight: 1.2,
              color: '#001A44',
              mb: 3,
              textTransform: 'uppercase',
            }}
          >
            {partners.title}
          </Typography>

          {/* Orange line - Using common component */}
          <Box id='partners-orange-line' data-component='partners-orange-line'>
            <OrangeLine width='162px' height='5px' mx='none' />
          </Box>
        </Box>

        {/* Partners Auto-Slider - CSS Animation with Next.js Image */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            backgroundColor: 'white',
            py: 3,
          }}
        >
          <Box
            id='partners-slider'
            data-component='partners-slider'
            sx={{
              display: 'flex',
              width: 'max-content',
              animation: partners.autoPlay
                ? 'slide 25s linear infinite'
                : 'none',
              '@keyframes slide': {
                '0%': {
                  transform: 'translateX(0)',
                },
                '100%': {
                  transform: 'translateX(-50%)',
                },
              },
            }}
          >
            {/* Duplicate list so it loops seamlessly */}
            {[...partnerItems, ...partnerItems].map((partner, index) => (
              <Box
                key={`${partner.id}-${index}`}
                id={`partner-item-${partner.id}-${index}`}
                data-component='partner-item'
                data-partner-id={partner.id}
                data-partner-index={index}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '12px', // Figma spec: 12px padding
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  transition: 'transform 0.2s ease',
                  minWidth: 'fit-content',
                  px: '48px',
                }}
              >
                {partner.logo ? (
                  <Image
                    id={`partner-logo-${partner.id}-${index}`}
                    data-component='partner-logo'
                    src={getImageUrl(partner.logo)}
                    alt={partner.name}
                    width={partner.logo.width}
                    height={partner.logo.height}
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 100,
                      height: 56,
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999',
                      fontSize: '12px',
                    }}
                  >
                    No Logo
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default PartnersSection;
